import React, { useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area, ComposedChart
} from 'recharts';

const COLORS = ['#10B981', '#EF4444', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4', '#14B8A6'];

function analyzeData(sheet, remarks) {
  const { headers, rows, colTypes } = sheet;
  if (!rows || rows.length === 0) return null;

  // Helper to resolve rowKeys identical to parent page viewer
  const getRowKey = (row, headers, index) => {
    const keyHeaders = ['rma #', 'rma no', 'rma number', 'rma', 'serial #', 'serial no', 'serial number', 'serial'];
    for (const kh of keyHeaders) {
      const headerIdx = headers.findIndex(h => h.toLowerCase().trim() === kh);
      if (headerIdx !== -1 && row[headerIdx] !== undefined && row[headerIdx] !== null && String(row[headerIdx]).trim() !== '') {
        return String(row[headerIdx]).trim();
      }
    }
    return `row-${index}`;
  };

  // 1. Process Audit Status from remarks list
  let approvedCount = 0;
  let rejectedCount = 0;
  let pendingCount = 0;

  if (remarks && remarks.length > 0) {
    const rowStatuses = {};
    const vendorRemarks = remarks.filter(r => r.type === 'VENDOR');
    // Map unique rowKeys to latest status
    vendorRemarks.forEach(r => {
      if (r.status) {
        rowStatuses[r.rowKey] = r.status;
      }
    });

    rows.forEach((row, ri) => {
      const key = getRowKey(row, headers, ri);
      const status = rowStatuses[key];
      if (status === 'APPROVED') approvedCount++;
      else if (status === 'REJECTED') rejectedCount++;
      else pendingCount++;
    });
  } else {
    pendingCount = rows.length;
  }

  const auditData = [
    { name: 'Approved', value: approvedCount, color: '#10B981' },
    { name: 'Rejected', value: rejectedCount, color: '#EF4444' },
    { name: 'Pending Review', value: pendingCount, color: '#F59E0B' }
  ].filter(d => d.value > 0);

  // 2. Identify Primary Numerical Column
  let primaryNumIdx = -1;
  const numColIndices = colTypes.map((t, i) => (t === 'number' || t === 'currency' ? i : -1)).filter(i => i !== -1);
  if (numColIndices.length > 0) {
    const currencyIdx = numColIndices.find(i => colTypes[i] === 'currency');
    if (currencyIdx !== undefined) {
      primaryNumIdx = currencyIdx;
    } else {
      const targetNumNames = ['amount', 'total', 'price', 'cost', 'qty', 'quantity', 'value', 'balance', 'revenue'];
      const bestIdx = numColIndices.find(i => targetNumNames.some(tn => headers[i].toLowerCase().includes(tn)));
      if (bestIdx !== undefined) primaryNumIdx = bestIdx;
    }
  }

  // 3. Find Turnaround Time (TAT) column
  const tatIdx = headers.findIndex(h => {
    const name = h.toLowerCase();
    return name.includes('tat') || name.includes('turnaround') || name.includes('turn around') || name.includes('sla') || name.includes('duration');
  });

  let tatData = [];
  if (tatIdx !== -1) {
    const tatGroups = {};
    rows.forEach(r => {
      let val = String(r[tatIdx] || 'In Process').trim();
      if (!val || val === '—' || val === '-') val = 'In Process';
      tatGroups[val] = (tatGroups[val] || 0) + 1;
    });
    tatData = Object.keys(tatGroups).map(k => ({
      name: k,
      count: tatGroups[k]
    })).sort((a, b) => b.count - a.count);
  }

  // 4. Find Category/Product column
  const catIdx = headers.findIndex(h => {
    const name = h.toLowerCase();
    return name.includes('category') || name.includes('product') || name.includes('item') || name.includes('part') || name.includes('model') || name.includes('brand');
  });

  let categoryData = [];
  let categoryHeader = '';
  if (catIdx !== -1) {
    categoryHeader = headers[catIdx];
    const catGroups = {};
    rows.forEach(r => {
      let val = String(r[catIdx] || 'Other').trim();
      if (!val || val === '—' || val === '-') val = 'Other';
      if (!catGroups[val]) catGroups[val] = { name: val, count: 0, totalVal: 0 };
      catGroups[val].count += 1;
      if (primaryNumIdx !== -1) {
        let n = parseFloat(String(r[primaryNumIdx]).replace(/[,$%₹€£]/g, ''));
        if (!isNaN(n)) catGroups[val].totalVal += n;
      }
    });
    categoryData = Object.values(catGroups).sort((a, b) => b.count - a.count).slice(0, 10);
  }

  // 5. Time Series Trend Data
  let dateColIdx = colTypes.findIndex(t => t === 'date');
  let timeSeriesData = [];
  let dateHeader = '';

  const normalizeDate = (str) => {
    if (!str) return null;
    let s = String(str).substring(0, 10).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const match = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    if (match) return `${match[3]}-${match[2]}-${match[1]}`;
    return s;
  };

  if (dateColIdx !== -1) {
    dateHeader = headers[dateColIdx];
    const dateGroups = {};
    rows.forEach(r => {
      const val = r[dateColIdx];
      if (val) {
        const day = normalizeDate(val);
        if (!day) return;
        if (!dateGroups[day]) dateGroups[day] = { date: day, count: 0, totalVal: 0 };
        dateGroups[day].count += 1;
        if (primaryNumIdx !== -1) {
          let n = parseFloat(String(r[primaryNumIdx]).replace(/[,$%₹€£]/g, ''));
          if (!isNaN(n)) dateGroups[day].totalVal += n;
        }
      }
    });
    timeSeriesData = Object.values(dateGroups).sort((a, b) => a.date.localeCompare(b.date));
  }

  // 6. Branch / Location Data
  const locIdx = headers.findIndex(h => {
    const name = h.toLowerCase().trim();
    return name.includes('location') || name.includes('city') || name.includes('site') || name.includes('branch') || name.includes('warehouse') || name.includes('state') || name.includes('region') || name.includes('center') || name.includes('hub');
  });

  let locationData = [];
  let locationHeader = '';
  if (locIdx !== -1) {
    locationHeader = headers[locIdx];
    const locGroups = {};
    rows.forEach(r => {
      let val = String(r[locIdx] || 'Other').trim();
      if (!val || val === '—' || val === '-') val = 'Other';
      if (!locGroups[val]) locGroups[val] = { name: val, count: 0 };
      locGroups[val].count += 1;
    });
    locationData = Object.values(locGroups).sort((a, b) => b.count - a.count).slice(0, 10);
  }

  // 7. Model / Item Part Data
  const modelIdx = headers.findIndex(h => {
    const name = h.toLowerCase().trim();
    return name.includes('model') || name.includes('part number') || name.includes('part no') || name.includes('part#') || name.includes('sku') || name.includes('device') || name.includes('item description') || name.includes('product name') || name.includes('model#');
  });

  let modelData = [];
  let modelHeader = '';
  if (modelIdx !== -1) {
    modelHeader = headers[modelIdx];
    const modelGroups = {};
    rows.forEach(r => {
      let val = String(r[modelIdx] || 'Other').trim();
      if (!val || val === '—' || val === '-') val = 'Other';
      if (!modelGroups[val]) modelGroups[val] = { name: val, count: 0 };
      modelGroups[val].count += 1;
    });
    modelData = Object.values(modelGroups).sort((a, b) => b.count - a.count).slice(0, 10);
  }

  return {
    auditData,
    tatData,
    tatHeader: tatIdx !== -1 ? headers[tatIdx] : '',
    categoryData,
    categoryHeader,
    locationData,
    locationHeader,
    modelData,
    modelHeader,
    timeSeriesData,
    dateHeader,
    numHeader: primaryNumIdx !== -1 ? headers[primaryNumIdx] : '',
    hasNumeric: primaryNumIdx !== -1,
    totalRows: rows.length,
    numCols: headers.length,
    approvedCount,
    rejectedCount,
    pendingCount
  };
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: 'var(--bg-card)', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 1000 }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: 'var(--text-primary)', fontSize: '12px' }}>{label || payload[0].name}</p>
        {payload.map((entry, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', fontSize: '12px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: entry.color || entry.fill }} />
            <span style={{ color: 'var(--text-secondary)' }}>{entry.name}:</span>
            <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
              {entry.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function ReportAnalytics({ sheet, remarks }) {
  const analysis = useMemo(() => analyzeData(sheet, remarks), [sheet, remarks]);

  if (!analysis) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
        <p>No analytical categories detected on this sheet.</p>
      </div>
    );
  }

  const {
    auditData,
    tatData,
    tatHeader,
    categoryData,
    categoryHeader,
    locationData,
    locationHeader,
    modelData,
    modelHeader,
    timeSeriesData,
    dateHeader,
    numHeader,
    hasNumeric,
    totalRows,
    approvedCount,
    rejectedCount,
    pendingCount
  } = analysis;

  const auditPercent = totalRows > 0 ? ((approvedCount + rejectedCount) / totalRows * 100).toFixed(0) : 0;

  return (
    <div style={{ padding: '24px 32px', overflowY: 'auto', flex: 1, background: 'var(--bg)' }}>
      {/* Dynamic welcome summary */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.01em' }}>
            RMA Insights dashboard
          </h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
            Real-time analytics computed across active worksheet rows.
          </p>
        </div>
        <span style={{ fontSize: '12px', fontWeight: 700, background: '#EFF6FF', color: '#1D4ED8', padding: '6px 14px', borderRadius: '99px' }}>
          📊 Active Sheet: {sheet.name}
        </span>
      </div>

      {/* Summary KPI Widgets */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
        <div style={{ background: 'var(--bg-card)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Total RMA Items</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>{totalRows.toLocaleString()}</div>
        </div>

        <div style={{ background: 'var(--bg-card)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ fontSize: '11px', color: '#10B981', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Approved Items</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#10B981', fontFamily: 'var(--font-display)' }}>{approvedCount.toLocaleString()}</div>
        </div>

        <div style={{ background: 'var(--bg-card)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ fontSize: '11px', color: '#EF4444', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Rejected Items</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#EF4444', fontFamily: 'var(--font-display)' }}>{rejectedCount.toLocaleString()}</div>
        </div>

        <div style={{ background: 'var(--bg-card)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ fontSize: '11px', color: '#F59E0B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Pending Audit</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#F59E0B', fontFamily: 'var(--font-display)' }}>{pendingCount.toLocaleString()}</div>
        </div>
      </div>

      {/* Dynamic Graph Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '24px' }}>
        
        {/* Graph 1: Audit Status Donut (Always Relevant to Viewers) */}
        <div style={{ display: 'flex', flexDirection: 'column', height: 340, background: 'var(--bg-card)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            Audit Progress Share
          </h3>
          <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={auditData}
                  cx="50%" cy="50%"
                  innerRadius={65}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                >
                  {auditData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            
            {/* Center Label for progress */}
            <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <span style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
                {auditPercent}%
              </span>
              <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Audited
              </span>
            </div>
          </div>
          
          {/* Custom Legends */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: '16px', fontSize: 11 }}>
            {auditData.map(d => (
              <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color }}></span>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{d.name} ({d.value})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Graph 2: TAT SLA Performance */}
        {tatData.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', height: 340, background: 'var(--bg-card)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '20px', color: 'var(--text-secondary)' }}>
              TAT SLA Performance ({tatHeader})
            </h3>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tatData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" opacity={0.5} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-secondary)', fontWeight: 500 }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,112,67,0.02)' }} />
                  <Bar dataKey="count" name="Items Count" fill="#3B82F6" radius={[6, 6, 0, 0]} maxBarSize={45}>
                    {tatData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Graph 3: Product Category Volume Chart */}
        {categoryData.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', height: 340, background: 'var(--bg-card)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '20px', color: 'var(--text-secondary)' }}>
              Top RMA Product Categories ({categoryHeader})
            </h3>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryData} layout="vertical" margin={{ top: 5, right: 15, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" opacity={0.5} />
                  <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: 'var(--text-primary)', fontWeight: 500 }} tickLine={false} width={80} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="RMA Quantity" fill="#8B5CF6" radius={[0, 4, 4, 0]} barSize={10} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Graph 5: Branch / Location Volume Chart */}
        {locationData.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', height: 340, background: 'var(--bg-card)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '20px', color: 'var(--text-secondary)' }}>
              Location/Branch volume ({locationHeader})
            </h3>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={locationData} layout="vertical" margin={{ top: 5, right: 15, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" opacity={0.5} />
                  <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: 'var(--text-primary)', fontWeight: 500 }} tickLine={false} width={110} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="RMA Quantity" fill="#06B6D4" radius={[0, 4, 4, 0]} barSize={10} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Graph 6: Top Models Chart */}
        {modelData.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', height: 340, background: 'var(--bg-card)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '20px', color: 'var(--text-secondary)' }}>
              Top Product Models ({modelHeader})
            </h3>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={modelData} layout="vertical" margin={{ top: 5, right: 15, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" opacity={0.5} />
                  <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: 'var(--text-primary)', fontWeight: 500 }} tickLine={false} width={110} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="RMA Quantity" fill="#3B82F6" radius={[0, 4, 4, 0]} barSize={10} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Graph 4: Time Series Area Trend (Full Width) */}
        {timeSeriesData.length > 0 && (
          <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', height: 360, background: 'var(--bg-card)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '20px', color: 'var(--text-secondary)' }}>
              RMA intake trend ({dateHeader})
            </h3>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={timeSeriesData} margin={{ top: 10, right: 15, left: -20, bottom: 10 }}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="var(--accent)" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" opacity={0.5} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
                  {hasNumeric && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />}
                  
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" height={36} iconType="circle" />
                  
                  <Area yAxisId="left" type="monotone" dataKey="count" name="RMA Items Count" stroke="var(--accent)" fillOpacity={1} fill="url(#colorCount)" strokeWidth={3} />
                  {hasNumeric && <Line yAxisId="right" type="monotone" dataKey="totalVal" name={`Total Value (${numHeader})`} stroke="#10b981" strokeWidth={2} dot={{r:2}} activeDot={{r:5}} />}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

      </div>

      {/* Pivot Table Analysis */}
      <div style={{ marginTop: '28px' }}>
        <PivotTable sheet={sheet} remarks={remarks} />
      </div>
    </div>
  );
}

// ── Interactive Pivot Table ──────────────────────────────────────────────────
function PivotTable({ sheet, remarks }) {
  const { headers, rows, colTypes } = sheet;

  const getRowKey = (row, headers, index) => {
    const keyHeaders = ['rma #', 'rma no', 'rma number', 'rma', 'serial #', 'serial no', 'serial number', 'serial'];
    for (const kh of keyHeaders) {
      const headerIdx = headers.findIndex(h => h.toLowerCase().trim() === kh);
      if (headerIdx !== -1 && row[headerIdx] !== undefined && row[headerIdx] !== null && String(row[headerIdx]).trim() !== '') {
        return String(row[headerIdx]).trim();
      }
    }
    return `row-${index}`;
  };

  const getRowAuditStatus = (row, ri, headers, remarks) => {
    if (!remarks || remarks.length === 0) return 'Pending Review';
    const vendorRemarks = remarks.filter(r => r.type === 'VENDOR');
    const rowKey = getRowKey(row, headers, ri);
    const remarksForRow = vendorRemarks.filter(r => r.rowKey === rowKey);
    if (remarksForRow.length === 0) return 'Pending Review';
    const latest = remarksForRow[remarksForRow.length - 1];
    if (latest.status === 'APPROVED') return 'Approved';
    if (latest.status === 'REJECTED') return 'Rejected';
    return 'Pending Review';
  };

  // Dimensions: allow pivoting on ANY column in the sheet, plus Audit Status
  const dimensions = useMemo(() => {
    const list = headers.map((h, idx) => ({ label: h, key: idx, isCalculated: false }));
    list.push({ label: 'Audit Status (Remarks)', key: 'audit_status', isCalculated: true });
    return list;
  }, [headers]);

  // Aggregation operators
  const aggregations = [
    { label: 'Count (Items)', key: 'count' },
    { label: 'Sum (Total)', key: 'sum' },
    { label: 'Average (Mean)', key: 'avg' },
    { label: 'Maximum', key: 'max' },
    { label: 'Minimum', key: 'min' }
  ];

  // Numeric fields candidate for mathematical aggregations
  const numericColumns = useMemo(() => {
    return headers
      .map((h, idx) => ({ label: h, index: idx, type: colTypes[idx] }))
      .filter(c => c.type === 'number' || c.type === 'currency');
  }, [headers, colTypes]);

  // State hooks
  const [rowDimIdx, setRowDimIdx] = useState(0);
  const [colDimIdx, setColDimIdx] = useState(dimensions.length > 0 ? dimensions.length - 1 : 0);
  const [aggIdx, setAggIdx] = useState(0);
  const [valColIdx, setValColIdx] = useState(0);
  const [rowSearch, setRowSearch] = useState('');
  const [sortBy, setSortBy] = useState('label-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Reset page to 1 on filter/dimension change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [rowDimIdx, colDimIdx, aggIdx, valColIdx, rowSearch, sortBy]);

  // Reset indices bounds on sheet dimension array changes
  React.useEffect(() => {
    setRowDimIdx(0);
    setColDimIdx(dimensions.length > 0 ? dimensions.length - 1 : 0);
    setValColIdx(0);
    setAggIdx(0);
    setCurrentPage(1);
  }, [dimensions]);

  const rowDim = dimensions[rowDimIdx];
  const colDim = dimensions[colDimIdx];
  const agg = aggregations[aggIdx];
  const valCol = numericColumns[valColIdx];

  const pivot = useMemo(() => {
    if (!rowDim || !colDim || !agg) return null;

    const rowValGetter = (row, ri) => {
      if (rowDim.isCalculated && rowDim.key === 'audit_status') {
        return getRowAuditStatus(row, ri, headers, remarks);
      }
      return String(row[rowDim.key] || 'Other').trim() || 'Other';
    };

    const colValGetter = (row, ri) => {
      if (colDim.isCalculated && colDim.key === 'audit_status') {
        return getRowAuditStatus(row, ri, headers, remarks);
      }
      return String(row[colDim.key] || 'Other').trim() || 'Other';
    };

    const metricValueGetter = (row) => {
      if (agg.key === 'count') return 1;
      if (!valCol) return 0;
      const rawVal = row[valCol.index];
      const val = parseFloat(String(rawVal || '0').replace(/[,$%₹€£]/g, ''));
      return isNaN(val) ? 0 : val;
    };

    const rowKeys = new Set();
    const colKeys = new Set();
    const cellGroups = {};

    rows.forEach((row, ri) => {
      const rKey = rowValGetter(row, ri);
      const cKey = colValGetter(row, ri);
      const mVal = metricValueGetter(row);

      rowKeys.add(rKey);
      colKeys.add(cKey);

      const cellKey = `${rKey}||${cKey}`;
      if (!cellGroups[cellKey]) cellGroups[cellKey] = [];
      cellGroups[cellKey].push(mVal);
    });

    const sortedRowKeys = Array.from(rowKeys).sort();
    const sortedColKeys = Array.from(colKeys).sort();

    const cells = {};
    const rowTotals = {};
    const colTotals = {};

    const cellRowValues = {};
    const cellColValues = {};
    const allValues = [];

    sortedRowKeys.forEach(r => { cellRowValues[r] = []; });
    sortedColKeys.forEach(c => { cellColValues[c] = []; });

    sortedRowKeys.forEach(r => {
      sortedColKeys.forEach(c => {
        const vals = cellGroups[`${r}||${c}`] || [];
        let aggVal = 0;
        if (vals.length > 0) {
          if (agg.key === 'count') aggVal = vals.length;
          else if (agg.key === 'sum') aggVal = vals.reduce((s, v) => s + v, 0);
          else if (agg.key === 'avg') aggVal = vals.reduce((s, v) => s + v, 0) / vals.length;
          else if (agg.key === 'max') aggVal = Math.max(...vals);
          else if (agg.key === 'min') aggVal = Math.min(...vals);

          vals.forEach(v => {
            cellRowValues[r].push(v);
            cellColValues[c].push(v);
            allValues.push(v);
          });
        }
        cells[`${r}||${c}`] = aggVal;
      });
    });

    const runAgg = (arr) => {
      if (arr.length === 0) return 0;
      if (agg.key === 'count') return arr.length;
      if (agg.key === 'sum') return arr.reduce((s, v) => s + v, 0);
      if (agg.key === 'avg') return arr.reduce((s, v) => s + v, 0) / arr.length;
      if (agg.key === 'max') return Math.max(...arr);
      if (agg.key === 'min') return Math.min(...arr);
      return 0;
    };

    sortedRowKeys.forEach(r => {
      rowTotals[r] = runAgg(cellRowValues[r]);
    });
    sortedColKeys.forEach(c => {
      colTotals[c] = runAgg(cellColValues[c]);
    });
    const grandTotal = runAgg(allValues);

    return {
      rows: sortedRowKeys,
      cols: sortedColKeys,
      cells,
      rowTotals,
      colTotals,
      grandTotal
    };
  }, [rowDim, colDim, agg, valCol, rows, headers, remarks]);

  // Sorting logic
  const sortedRowsList = useMemo(() => {
    if (!pivot) return [];
    let keys = [...pivot.rows];
    if (sortBy === 'label-asc') {
      keys.sort((a, b) => a.localeCompare(b));
    } else if (sortBy === 'label-desc') {
      keys.sort((a, b) => b.localeCompare(a));
    } else if (sortBy === 'value-desc') {
      keys.sort((a, b) => (pivot.rowTotals[b] || 0) - (pivot.rowTotals[a] || 0));
    } else if (sortBy === 'value-asc') {
      keys.sort((a, b) => (pivot.rowTotals[a] || 0) - (pivot.rowTotals[b] || 0));
    }
    return keys;
  }, [pivot, sortBy]);

  // Filtering logic
  const filteredRowsList = useMemo(() => {
    return sortedRowsList.filter(k => k.toLowerCase().includes(rowSearch.toLowerCase()));
  }, [sortedRowsList, rowSearch]);

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredRowsList.slice(startIndex, startIndex + pageSize);
  }, [filteredRowsList, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredRowsList.length / pageSize) || 1;

  const exportCsv = () => {
    if (!pivot) return;
    let csvContent = "data:text/csv;charset=utf-8,";
    
    // Header row
    const colHeader = [rowDim.label, ...pivot.cols, "Total"].map(c => `"${c.replace(/"/g, '""')}"`).join(",");
    csvContent += colHeader + "\r\n";
    
    // Data rows
    sortedRowsList.forEach(r => {
      const rowVals = [
        r,
        ...pivot.cols.map(c => pivot.cells[`${r}||${c}`] || 0),
        pivot.rowTotals[r]
      ];
      csvContent += rowVals.map(v => typeof v === 'string' ? `"${v.replace(/"/g, '""')}"` : v).join(",") + "\r\n";
    });
    
    // Totals row
    const totalRow = [
      "Grand Total",
      ...pivot.cols.map(c => pivot.colTotals[c] || 0),
      pivot.grandTotal
    ];
    csvContent += totalRow.map(v => typeof v === 'string' ? `"${v.replace(/"/g, '""')}"` : v).join(",") + "\r\n";
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `pivot_${rowDim.label.toLowerCase()}_vs_${colDim.label.toLowerCase()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!pivot || dimensions.length === 0) return null;

  return (
    <div style={{ background: 'var(--bg-card)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: '20px' }}>
        <div>
          <h3 style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
            🧮 Interactive Pivot Table Analyzer
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
            Cross-tabulate data rows on demand. Configure row headers, column headers, and metrics.
          </p>
        </div>
        <button 
          onClick={exportCsv}
          className="btn btn-secondary" 
          style={{ gap: 6, fontSize: '12px', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', fontWeight: 700 }}
        >
          📥 Export CSV
        </button>
      </div>

      {/* Selectors Panel */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, padding: '18px', background: 'var(--bg)', borderRadius: '14px', border: '1px solid var(--border)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rows (Dimensions)</label>
          <select 
            value={rowDimIdx} 
            onChange={(e) => setRowDimIdx(parseInt(e.target.value))}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--bg-card)', color: 'var(--text-primary)', fontWeight: 600, outline: 'none' }}
          >
            {dimensions.map((d, idx) => (
              <option key={idx} value={idx}>{d.label}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Columns (Dimensions)</label>
          <select 
            value={colDimIdx} 
            onChange={(e) => setColDimIdx(parseInt(e.target.value))}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--bg-card)', color: 'var(--text-primary)', fontWeight: 600, outline: 'none' }}
          >
            {dimensions.map((d, idx) => (
              <option key={idx} value={idx}>{d.label}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aggregation Type</label>
          <select 
            value={aggIdx} 
            onChange={(e) => setAggIdx(parseInt(e.target.value))}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--bg-card)', color: 'var(--text-primary)', fontWeight: 600, outline: 'none' }}
          >
            {aggregations.map((a, idx) => (
              <option key={idx} value={idx}>{a.label}</option>
            ))}
          </select>
        </div>

        {agg.key !== 'count' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Value Field (Maths)</label>
            {numericColumns.length > 0 ? (
              <select 
                value={valColIdx} 
                onChange={(e) => setValColIdx(parseInt(e.target.value))}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--bg-card)', color: 'var(--text-primary)', fontWeight: 600, outline: 'none' }}
              >
                {numericColumns.map((c, idx) => (
                  <option key={idx} value={idx}>{c.label}</option>
                ))}
              </select>
            ) : (
              <span style={{ fontSize: '11px', color: '#EF4444', fontWeight: 600, padding: '8px 0' }}>⚠️ No numeric fields detected</span>
            )}
          </div>
        )}
      </div>

      {/* Filters Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ position: 'relative', width: '280px' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            placeholder={`Filter ${rowDim?.label}...`}
            value={rowSearch}
            onChange={(e) => setRowSearch(e.target.value)}
            style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sort Rows By</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '12px', background: 'var(--bg-card)', color: 'var(--text-primary)', fontWeight: 600, outline: 'none' }}
          >
            <option value="label-asc">Row Name (A to Z)</option>
            <option value="label-desc">Row Name (Z to A)</option>
            <option value="value-desc">Total Value (High to Low)</option>
            <option value="value-asc">Total Value (Low to High)</option>
          </select>
        </div>
      </div>

      {/* Grid Table Container */}
      <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid var(--border)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                {rowDim?.label}
              </th>
              {pivot.cols.map(c => (
                <th key={c} style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'right' }}>
                  {c}
                </th>
              ))}
              <th style={{ padding: '12px 16px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'right', background: 'rgba(255, 112, 67, 0.05)' }}>
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.length > 0 ? (
              paginatedRows.map(r => (
                <tr key={r} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.15s' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {r}
                  </td>
                  {pivot.cols.map(c => {
                    const val = pivot.cells[`${r}||${c}`] || 0;
                    return (
                      <td key={c} style={{ padding: '12px 16px', textAlign: 'right', color: 'var(--text-secondary)' }}>
                        {val.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                    );
                  })}
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: 'var(--text-primary)', background: 'rgba(255, 112, 67, 0.02)' }}>
                    {(pivot.rowTotals[r] || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={pivot.cols.length + 2} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  No matching row results found.
                </td>
              </tr>
            )}
            {/* Grand Totals row */}
            <tr style={{ background: 'rgba(241, 245, 249, 0.5)', fontWeight: 700 }}>
              <td style={{ padding: '14px 16px', color: 'var(--text-primary)' }}>
                Grand Total
              </td>
              {pivot.cols.map(c => (
                <td key={c} style={{ padding: '14px 16px', textAlign: 'right', color: 'var(--text-primary)' }}>
                  {(pivot.colTotals[c] || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </td>
              ))}
              <td style={{ padding: '14px 16px', textAlign: 'right', color: 'var(--accent)', background: 'rgba(255, 112, 67, 0.05)', fontSize: '13px' }}>
                {pivot.grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', padding: '10px 16px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--border)', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Showing <strong>{(currentPage - 1) * pageSize + 1}</strong> to <strong>{Math.min(currentPage * pageSize, filteredRowsList.length)}</strong> of <strong>{filteredRowsList.length}</strong> records
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, borderRadius: '6px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              ◀ Previous
            </button>
            <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, borderRadius: '6px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next ▶
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
